import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@doccareplus/i18n';
import { Button, Input, OtpInput, PhoneInput } from '@doccareplus/ui';
import { detectDefaultCountry, dialCodeFor, isValidNationalPhoneNumber, nationalPhoneLengthLimit, toE164 } from '@doccareplus/shared/lib/phone';
import { useLoginWithOtpCommand, useRegisterPatientCommand, useSendOtpCommand } from '../application/auth.command';

const RESEND_COOLDOWN_SECONDS = 60;

function errorText(error: unknown, fallback: string): string {
  const message = (error as { message?: string } | null)?.message;
  return message && message.length > 0 ? message : fallback;
}

// The passwordless phone + WhatsApp-OTP flow, shared by the login and register pages. `mode` decides whether a
// name is collected and which command (login vs register) + OTP purpose is used — the pages themselves stay
// thin and separate (no in-page tab).
export function PhoneOtpForm({ mode }: { mode: 'login' | 'signup' }) {
  const { t } = useTranslation('portal');
  const navigate = useNavigate();

  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [country, setCountry] = useState(() => detectDefaultCountry());
  const [national, setNational] = useState('');
  const [fullName, setFullName] = useState('');
  const [otp, setOtp] = useState('');
  const [cooldown, setCooldown] = useState(0);

  const sendOtp = useSendOtpCommand();
  const login = useLoginWithOtpCommand();
  const register = useRegisterPatientCommand();

  const phoneE164 = useMemo(() => {
    try {
      return toE164(dialCodeFor(country) ?? '', national);
    } catch {
      return null;
    }
  }, [country, national]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const purpose = mode === 'signup' ? 'Signup' : 'Login';
  const nameMissing = mode === 'signup' && fullName.trim().length === 0;
  const phoneValid = isValidNationalPhoneNumber(country, national);
  const busy = sendOtp.isPending || login.isPending || register.isPending;

  const handleSendOtp = async () => {
    if (!phoneE164 || !phoneValid || nameMissing) return;
    await sendOtp.mutateAsync({ phoneE164, purpose });
    setStep('otp');
    setOtp('');
    setCooldown(RESEND_COOLDOWN_SECONDS);
  };

  const handleVerify = async () => {
    if (!phoneE164 || otp.length < 6) return;
    if (mode === 'signup') {
      await register.mutateAsync({ phoneE164, otpCode: otp, fullName: fullName.trim() });
    } else {
      await login.mutateAsync({ phoneE164, otpCode: otp });
    }
    navigate('/', { replace: true });
  };

  const sendError = sendOtp.error;
  const verifyError = mode === 'signup' ? register.error : login.error;

  if (step === 'phone') {
    return (
      <div className="flex flex-col gap-4">
        {mode === 'signup' && (
          <Input
            label={t('auth.fullName', 'Full name')}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder={t('auth.fullNamePlaceholder', 'e.g. Arjun Kapoor')}
            maxLength={150}
          />
        )}
        <PhoneInput
          label={t('auth.phone', 'Phone number')}
          country={country}
          onCountryChange={setCountry}
          value={national}
          onChange={setNational}
          maxLength={nationalPhoneLengthLimit(country)}
          requiredMark
          countryButtonLabel={t('auth.selectCountry', 'Select country code')}
          searchPlaceholder={t('common.search', 'Search')}
          noResultsLabel={t('common.noResults', 'No results')}
        />
        {sendError ? (
          <p className="text-[12.5px] font-medium text-[var(--danger)]">
            {errorText(sendError, t('auth.sendFailed', 'Could not send the code. Try again.'))}
          </p>
        ) : null}
        <Button variant="primary" fullWidth isLoading={sendOtp.isPending} disabled={!phoneValid || nameMissing} onClick={handleSendOtp}>
          {t('auth.sendCode', 'Send code')}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[13px] text-[var(--text-2)]">
        {t('auth.otpSentTo', 'We sent a WhatsApp code to')} <span className="font-semibold text-[var(--text)]">{phoneE164}</span>
      </p>
      <OtpInput
        label={t('auth.code', 'Verification code')}
        value={otp}
        onChange={setOtp}
        nativeInputLabel={t('auth.code', 'Verification code')}
      />
      {verifyError ? (
        <p className="text-[12.5px] font-medium text-[var(--danger)]">
          {errorText(verifyError, t('auth.verifyFailed', 'That code did not work. Try again.'))}
        </p>
      ) : null}
      <Button variant="primary" fullWidth isLoading={login.isPending || register.isPending} disabled={otp.length < 6 || busy} onClick={handleVerify}>
        {mode === 'signup' ? t('auth.createAccount', 'Create account') : t('auth.continue', 'Continue')}
      </Button>
      <div className="flex items-center justify-between text-[12.5px]">
        <button type="button" className="font-medium text-[var(--text-3)]" onClick={() => setStep('phone')}>
          {t('auth.changeNumber', 'Change number')}
        </button>
        <button
          type="button"
          disabled={cooldown > 0 || sendOtp.isPending}
          className="font-semibold text-[var(--primary)] disabled:text-[var(--text-3)]"
          onClick={handleSendOtp}
        >
          {cooldown > 0 ? t('auth.resendIn', 'Resend in {{s}}s', { s: cooldown }) : t('auth.resend', 'Resend code')}
        </button>
      </div>
    </div>
  );
}
